import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  CardBox,
  CardActions,
  CardContent,
  Icons,
  Stack,
  Text,
  Row,
  useAlert,
  NotificationDialog,
} from '../../components'
import { mutations, queries } from '../../graphql'
import { CreditCard } from '../funds/contribute'

export function ManageCreditCards(props) {
  const { manageCreditCardsRef } = props
  const navigate = useNavigate()
  const { donorFundId } = useOutletContext() ?? {}
  const [open, setOpen] = useState(false)
  const [cannotDeleteOpen, setCannotDeleteOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const card = useQuery(queries.paymentMethods.myCreditCards, {
    skip: !donorFundId,
    variables: { fundId: donorFundId },
  })
  const myFund = useQuery(queries.funds.myFund)

  const [mutate, { isLoading }] = useMutation(mutations.paymentMethods.deactivateCreditCard, {
    onCompleted: () => {
      setAlert({ message: `Removed payment method.`, severity: 'success' })
      setDeletingId(null)
    },
    onError: (newErrors) => {
      setAlert({ message: `An error occured.`, severity: 'error' })
      setDeletingId(null)
    },
    refetchQueries: [{ query: queries.paymentMethods.myCreditCards, variables: { fundId: donorFundId } }],
  })

  const savedCards = card.data?.me?.fund?.creditCards?.filter((cc) => cc.status === 'active')
  const hasSavedCards = !!(savedCards && savedCards.length)

  const handleDelete = () => {
    mutate({
      variables: {
        deactivateCreditCardInput: {
          ids: [deletingId],
        },
      },
    })

    setOpen(false)
  }

  const handleConfirm = (id) => {
    setOpen(true)
    setDeletingId(id)
  }
  const handleDialogClose = () => {
    setOpen(false)
    setCannotDeleteOpen(false)
    setDeletingId(null)
  }
  const closeForm = () => {
    setIsAddingCard(false)
  }

  const savedCardsDisplay = (
    <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
      {hasSavedCards ? (
        savedCards.map((cc) => {
          const srcObj = cc.details.card ? cc.details.card : cc.details.sources.data[0]

          return (
            <Card
              key={cc.id}
              variant="outlined"
              style={{
                minWidth: 300,
                maxWidth: 300,
                margin: 10,
                backgroundColor: 'lightgrey',
                borderColor: 'lightgrey',
              }}
            >
              <CardContent>
                <Text.H5 sx={{ textTransform: 'capitalize' }}>{`${srcObj.brand}`}</Text.H5>
                <br />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Text.Body>{`•••• •••• •••• ${srcObj.last4}`}</Text.Body>
                  <Text.Body>{`exp: ${srcObj.exp_month}/${srcObj.exp_year}`}</Text.Body>
                </Stack>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  onClick={cc.canDelete ? () => handleConfirm(cc.id) : () => setCannotDeleteOpen(true)}
                >
                  <Icons.DeleteForever fontSize="small" />
                  Remove
                </Button>
              </CardActions>
            </Card>
          )
        })
      ) : (
        <Text.Body sx={{ display: 'inline' }}>
          No saved credit cards. Click{' '}
          <Text.Body
            sx={{
              color: '#0d6efd',
              textDecoration: 'underline',
              cursor: 'pointer',
              display: 'inline',
            }}
            onClick={() => setIsAddingCard(true)}
          >
            here
          </Text.Body>{' '}
          to add a credit card.
        </Text.Body>
      )}
    </Stack>
  )

  const bottomSection = hasSavedCards && (
    <Row
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box>
        <Text.Bold sx={{ display: 'inline' }}>Note: </Text.Bold>
        <Text.Body sx={{ display: 'inline' }}>Your payment information is encrypted and never shared.</Text.Body>
      </Box>
      <Button onClick={() => setIsAddingCard(true)}>Add another card</Button>
    </Row>
  )

  const addCardForm = <CreditCard addingFunds={false} closeForm={closeForm} />

  return (
    <Stack ref={manageCreditCardsRef}>
      <CardBox>
        <Stack>
          <Text.H4>Credit Cards</Text.H4>
          {isAddingCard && <Text.Body>Enter your card details.</Text.Body>}
        </Stack>
        <br />
        <Stack>
          {isAddingCard ? (
            addCardForm
          ) : (
            <>
              {savedCardsDisplay}
              {bottomSection}
            </>
          )}
        </Stack>
        <br />
        <NotificationDialog
          open={cannotDeleteOpen}
          onClose={handleDialogClose}
          type="alert"
          message={
            <>
              This credit card cannot be removed because it is linked to a scheduled or recurring contribution.
              <br />
              <br />
              To remove this payment method, please first cancel any scheduled or recurring contributions.
            </>
          }
          actions={[
            { label: 'Close', onClick: handleDialogClose },
            {
              label: 'Go to my activity',
              onClick: () => navigate(`/funds/${myFund.data?.me?.fund.id}/activity/giving-wallet`),
              autoFocus: true,
            },
          ]}
        />
        <NotificationDialog
          open={open}
          onClose={handleDialogClose}
          type="alert"
          title="Remove Credit Card"
          message="Are you sure you want to remove this credit card from your profile?"
          actions={[
            { label: 'Cancel', onClick: handleDialogClose },
            { label: 'Yes, remove it', onClick: handleDelete, autoFocus: true },
          ]}
        />
        <Alert {...alertProps} />
      </CardBox>
    </Stack>
  )
}
